import { useParams, Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { getCityBySlug } from "@/data/areas";
import { buildCityLocalBusinessSchema, generateBreadcrumbSchema } from "@/lib/structuredData";
import { MapPin, Phone, MessageCircle, CheckCircle2 } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/27732509394";

export default function CityHubPage() {
  const { city: citySlug } = useParams<{ city: string }>();
  const city = citySlug ? getCityBySlug(citySlug) : null;

  if (!city) return <NotFound />;

  const canonical = `/areas/${city.slug}`;
  const highPriority = city.suburbs.filter((s) => s.priority === "high");
  const standard = city.suburbs.filter((s) => s.priority === "standard");

  const structuredData = [
    buildCityLocalBusinessSchema(city.name, city.province, canonical),
    generateBreadcrumbSchema([
      { name: "Home", url: "https://bookkeepingservices.co.za/" },
      { name: "Areas We Serve", url: "https://bookkeepingservices.co.za/areas" },
      { name: city.name, url: `https://bookkeepingservices.co.za${canonical}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: city.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <SEO
        title={city.metaTitle}
        description={city.metaDescription}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Breadcrumbs
        items={[
          { name: "Areas We Serve" },
          { name: city.name },
        ]}
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 section-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-accent rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">
                {city.name}, {city.province}
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              {city.h1}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {city.intro}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                  <Phone className="w-5 h-5" />
                  Request a Quote
                </Button>
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="btn-outline-primary gap-2 text-base px-8 h-14">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-6 text-muted-foreground leading-relaxed">
            <h2 className="font-heading text-3xl font-bold text-foreground">
              Who we work with in {city.name}
            </h2>
            <p>{city.overview}</p>
            <p>{city.economy}</p>
          </div>
        </div>
      </section>

      {/* Business types */}
      <section className="py-20 section-light">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Businesses we assist"
            title={`Types of ${city.name} businesses we support`}
            description="Our packages fit a wide range of owner-managed businesses across the metro."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {city.businessTypes.map((b) => (
              <div key={b} className="flex items-start gap-3 bg-white p-5 rounded-xl border border-border">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suburb links */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Areas we serve"
            title={`Priority ${city.name} areas`}
            description={`We serve businesses throughout ${city.name}. Dedicated pages below are rolling out first for high-demand nodes.`}
          />
          <div className="max-w-5xl mx-auto">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
              {highPriority.map((s) => (
                <SuburbCard key={s.slug} citySlug={city.slug} suburb={s} />
              ))}
            </div>
            {standard.length > 0 && (
              <>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                  Additional {city.name} areas
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {standard.map((s) => (
                    <SuburbCard key={s.slug} citySlug={city.slug} suburb={s} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Services links */}
      <section className="py-20 section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
              Services included
            </h2>
            <p className="text-muted-foreground mb-8">
              Every {city.name} client has access to the full compliance stack.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { name: "Monthly Bookkeeping", href: "/monthly-bookkeeping-services" },
                { name: "Payroll (EMP201 & EMP501)", href: "/payroll-emp201-emp501" },
                { name: "VAT Returns", href: "/vat" },
                { name: "UIF Declarations", href: "/uif-monthly-declarations" },
                { name: "Annual Financial Statements", href: "/annual-financial-statements" },
                { name: "Annual Tax Returns", href: "/annual-tax-returns" },
                { name: "Provisional Tax", href: "/provisional-tax-returns" },
                { name: "CIPC Annual Returns", href: "/cipc-annual-returns" },
                { name: "Beneficial Ownership", href: "/beneficial-ownership-submission" },
                { name: "Workmen's Compensation", href: "/workmens-compensation" },
              ].map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  className="px-4 py-2 rounded-full border border-border bg-white text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              label="FAQ"
              title={`${city.name} bookkeeping questions`}
              description=""
            />
            <div className="space-y-4">
              {city.faqs.map((f) => (
                <details
                  key={f.q}
                  className="group bg-white border border-border rounded-xl p-5"
                >
                  <summary className="cursor-pointer font-heading font-semibold text-foreground list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-primary transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
}

function SuburbCard({
  citySlug,
  suburb,
}: {
  citySlug: string;
  suburb: { slug: string; name: string; published: boolean };
}) {
  const to = `/areas/${citySlug}/${suburb.slug}`;
  if (suburb.published) {
    return (
      <Link
        to={to}
        className="block p-4 rounded-xl border border-border bg-white hover:border-primary hover:shadow-sm transition-all"
      >
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground">{suburb.name}</span>
          <span className="text-primary text-sm">View →</span>
        </div>
      </Link>
    );
  }
  return (
    <div className="p-4 rounded-xl border border-dashed border-border bg-white/60">
      <div className="flex items-center justify-between">
        <span className="font-medium text-foreground">{suburb.name}</span>
        <span className="text-xs text-muted-foreground">Coming soon</span>
      </div>
    </div>
  );
}
